import { Controller } from '@hotwired/stimulus';

/**
 * Modale de configuration de la révision de prix d'un marché (Tâche 7, refondue
 * par la spec 2026-08-07 `revision-prix-mois-zero-apres-index.md`).
 *
 * Recalcule côté client, sans jamais écrire de couleur ni de SVG (contrainte
 * design — cf. précédent Gantt) : le JS ne pose QUE des data-attrs, des noms de
 * classes et du texte ; la coloration valide/invalide vient du CSS réagissant à
 * `data-revision-sum-valid` sur la cible `sumCheck`, et TOUTES les phrases
 * françaises descendent en `data-template*` depuis la vue (figer du français
 * ici échapperait à `ui-lint-erb.sh` comme à `lint-terminology.sh`, aucun des
 * deux ne lisant les `.js`).
 *
 * Recalcule :
 *   (a) la somme « partie fixe % + Σ poids % » et son état valide (== 100 %) ;
 *   (b) l'aperçu de formule `Cn = a + b₁ × BTₙ / base + …` ;
 *   (c) LA BORNE HAUTE du mois zéro (D-M0-4) — voir #refreshMonthBounds.
 * Ajoute et retire des rangées d'index (nested attributes `terms_attributes`),
 * EN MIROIR dans les deux zones (tableau des index + bloc des valeurs).
 *
 * ⚠️ LA SÉQUENCE EST LE SENS DU LOT : index → mois zéro → valeur. La valeur au
 * mois zéro DÉCOULE des deux premiers, elle ne peut donc plus s'afficher avant
 * eux, et le mois ne peut plus être choisi avant l'index dont il dépend (mesuré
 * le 2026-08-07 : 2 mois d'écart de publication entre BT49 et BT50).
 *
 * Targets:
 *   fixedPart        — input du pourcentage de partie fixe
 *   rows             — <tbody> des termes (index + poids)
 *   template         — <template> SIBLING du <form> (rangée d'index vierge)
 *   valueRows        — <tbody> des valeurs au mois zéro
 *   valueTemplate    — <template> SIBLING du <form> (ligne de valeur vierge)
 *   valuesBlock      — conteneur du bloc « Valeurs au mois zéro »
 *   valueRow         — une ligne de valeur (porte `data-term-index`)
 *   valueLabel       — le libellé de série d'une ligne de valeur
 *   baseValue        — l'input « Valeur au mois zéro » d'une ligne
 *   insee            — la pastille de provenance INSEE d'une ligne
 *   baseMonth/baseYear — les deux selects du mois zéro
 *   monthPrompt      — « Choisissez d'abord un index BT. »
 *   monthHint        — « Index BT49 publié jusqu'à juillet 2026. »
 *   monthWarning / monthWarningText — l'avertissement « aucune valeur INSEE… »
 *   sumCheck, formula — bandeau de somme et aperçu de formule
 *
 * Values:
 *   validLabel      — libellé court état valide (émis par la vue, réservé)
 *   invalidLabel    — libellé court état invalide (émis par la vue, réservé)
 *   lookupUrl       — URL JSON du store INSEE local (auto-remplissage).
 *                     Absente → l'auto-remplissage est silencieusement désactivé.
 *   publishedMonths — { 'BT49': '2026-07', … } dernier mois PUBLIÉ par série,
 *                     émis au rendu par `BtIndexValue.latest_published_months`.
 *                     ⚠️ Le JS ne fait AUCUNE requête pour borner : tout le
 *                     catalogue est déjà là, donc changer d'index re-borne sans
 *                     aller-retour. Une série absente de ce hash n'a AUCUNE
 *                     valeur publiée — elle ne borne rien, et c'est le socle
 *                     (#autofillRow) qui refusera la valeur.
 *
 * Actions:
 *   refresh   — recalcule somme + formule (câblé sur input->…#refresh)
 *   addRow    — clone les DEUX <template>, insère la paire, recalcule
 *   removeRow — retire la paire (ou marque _destroy), recalcule
 *
 * Auto-remplissage INSEE (délégué depuis `connect`, pas de data-action par
 * rangée) : `change` sur un `<select>` d'index remplit SA ligne de valeur ;
 * `change` sur les selects de mois/année re-remplit TOUTES les lignes.
 *
 * @example
 *   <div data-controller="price-revision-form"
 *        data-price-revision-form-published-months-value='{"BT49":"2026-07"}'>
 *     <tbody data-price-revision-form-target="rows"></tbody>
 *     <tbody data-price-revision-form-target="valueRows"></tbody>
 *     <template data-price-revision-form-target="template">…NEW_RECORD…</template>
 *     <template data-price-revision-form-target="valueTemplate">…NEW_RECORD…</template>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    'fixedPart',
    'rows',
    'template',
    'valueRows',
    'valueTemplate',
    'valuesBlock',
    'valueRow',
    'valueLabel',
    'baseValue',
    'insee',
    'baseMonth',
    'baseYear',
    'monthPrompt',
    'monthHint',
    'monthWarning',
    'monthWarningText',
    'sumCheck',
    'formula',
  ];
  static values = {
    validLabel: String,
    invalidLabel: String,
    lookupUrl: String,
    publishedMonths: Object,
  };

  connect() {
    // Les inputs de rangée (poids / valeur / série) ne portent PAS de
    // data-action dans les partials ; on délègue donc l'écoute sur la racine du
    // contrôleur. Symétrique avec disconnect.
    this.recompute = this.refresh.bind(this);
    this.onChange = this.#handleChange.bind(this);
    this.element.addEventListener('input', this.recompute);
    this.element.addEventListener('change', this.onChange);

    this.#refreshMonthBounds();
    this.refresh();
  }

  disconnect() {
    this.element.removeEventListener('input', this.recompute);
    this.element.removeEventListener('change', this.onChange);
  }

  // `change` délégué : la SÉQUENCE compte. Un changement d'index re-borne le
  // mois AVANT de tenter l'auto-remplissage — sinon on irait chercher une valeur
  // pour un mois que la nouvelle série ne publie pas encore.
  #handleChange(event) {
    const target = event.target;
    if (target.matches("select[name*='[bt_series_code]']")) {
      this.#syncValueLabel(target);
      this.#refreshMonthBounds();
      this.#autofillRow(this.#termIndexOf(target));
    } else if (target.matches("select[name*='base_month_']")) {
      // Le select MOIS dépend de l'ANNÉE choisie : la borne ne mord que sur
      // l'année de publication (en 2023 les douze mois sont publiés).
      this.#refreshMonthBounds();
      this.#autofillAllRows();
    }
    this.refresh();
  }

  addRow(event) {
    event.preventDefault();
    const index = new Date().getTime();
    this.rowsTarget.insertAdjacentHTML(
      'beforeend',
      this.templateTarget.innerHTML.replace(/NEW_RECORD/g, index)
    );
    // La ligne de valeur est posée EN MIROIR, avec le MÊME index : les deux
    // zones décrivent le même terme et doivent rester appariées.
    this.valueRowsTarget.insertAdjacentHTML(
      'beforeend',
      this.valueTemplateTarget.innerHTML.replace(/NEW_RECORD/g, index)
    );
    this.#refreshMonthBounds();
    this.refresh();
  }

  removeRow(event) {
    event.preventDefault();
    const row = event.target.closest('tr');
    if (!row) return;

    const index = row.dataset.termIndex;
    const destroyField = row.querySelector("input[name*='_destroy']");
    if (destroyField && row.querySelector("input[name*='[id]']")) {
      // Rangée persistée : la masquer + marquer _destroy plutôt que la retirer
      // du DOM (round-trip d'édition). La rangée reste donc DANS le <form> et
      // part avec la soumission — c'est `ConfigForm#destroy_flagged?` qui
      // l'écarte côté serveur, et rien d'autre. Ne PAS supposer que la
      // réconciliation par code BT suffit : elle ne détruit que les codes
      // ABSENTS du payload, et celui-ci est présent (bug QA 2026-08-06).
      destroyField.value = '1';
      row.classList.add('hidden');
      // ⚠️ La ligne de valeur, elle, est RETIRÉE et non masquée : elle ne porte
      // ni `id` ni `_destroy`, donc son `base_index_value` continuerait d'être
      // soumis pour un terme qu'on vient de marquer détruit — et un
      // `base_index_value` sans `bt_series_code` fait échouer la validation du
      // terme, sur une rangée que l'architecte croit supprimée.
      this.#valueRowFor(index)?.remove();
    } else {
      row.remove();
      this.#valueRowFor(index)?.remove();
    }
    this.#refreshMonthBounds();
    this.refresh();
  }

  refresh() {
    const fixed = this.#toNumber(this.fixedPartTarget.value);
    const terms = this.#visibleRows().map((row) => ({
      code: row.querySelector("select[name*='bt_series_code']")?.value || '',
      base: this.#baseValueFor(row.dataset.termIndex)?.value || '',
      weight: this.#toNumber(row.querySelector("input[name*='weight_percent']")?.value),
    }));

    this.#renderSum(fixed, terms);
    this.#renderFormula(fixed, terms);
  }

  #visibleRows() {
    return Array.from(this.rowsTarget.querySelectorAll('tr')).filter(
      (row) => row.querySelector("input[name*='_destroy']")?.value !== '1'
    );
  }

  #renderSum(fixed, terms) {
    const total = terms.reduce((sum, term) => sum + term.weight, fixed);
    const rounded = Math.round(total * 100) / 100;
    const weights = Math.round((total - fixed) * 100) / 100;
    const valid = Math.abs(rounded - 100) < 0.01;

    const message = valid
      ? `Partie fixe ${this.#fr(fixed)} % + poids ${this.#fr(weights)} % = 100 %`
      : `Partie fixe ${this.#fr(fixed)} % + poids ${this.#fr(weights)} % = ${this.#fr(rounded)} % (écart ${this.#fr(Math.round((rounded - 100) * 100) / 100)} %)`;

    // Écrit le message dans le <span> interne pour préserver l'icône rendue
    // serveur (jamais de <svg> injecté en JS). Fallback : la cible elle-même.
    const label = this.sumCheckTarget.querySelector('span') || this.sumCheckTarget;
    label.textContent = message;

    // Le CSS réagit à ce data-attr (`.revision-sum-check[data-revision-sum-valid]`).
    this.sumCheckTarget.dataset.revisionSumValid = valid ? 'true' : 'false';
  }

  #renderFormula(fixed, terms) {
    const parts = terms
      .filter((term) => term.code && term.base)
      .map(
        (term) => `${this.#fr(term.weight / 100)} × ${term.code}ₙ / ${term.base.replace('.', ',')}`
      );

    this.formulaTarget.textContent =
      parts.length === 0 ? 'Cn = …' : `Cn = ${this.#fr(fixed / 100)} + ${parts.join(' + ')}`;
  }

  // --- Appariement des deux zones -------------------------------------------

  #termIndexOf(node) {
    return node.closest('tr')?.dataset.termIndex;
  }

  #valueRowFor(index) {
    if (index === undefined) return null;
    return this.valueRowTargets.find((row) => row.dataset.termIndex === String(index)) || null;
  }

  #baseValueFor(index) {
    return this.#valueRowFor(index)?.querySelector("input[name*='[base_index_value]']") || null;
  }

  // Le libellé de la ligne de valeur suit la série choisie : sans ça le bloc
  // « Valeurs au mois zéro » annoncerait l'ancien index sous la nouvelle valeur.
  #syncValueLabel(select) {
    const row = this.#valueRowFor(this.#termIndexOf(select));
    const label = row?.querySelector("[data-price-revision-form-target='valueLabel']");
    if (label) label.textContent = select.selectedOptions[0]?.textContent?.trim() || '';
  }

  // --- Borne haute du mois zéro (D-M0-4) ------------------------------------

  // Codes de série actuellement retenus (rangées non marquées `_destroy`).
  #selectedCodes() {
    return this.#visibleRows()
      .map((row) => row.querySelector("select[name*='bt_series_code']")?.value || '')
      .filter((code) => code.length > 0);
  }

  // La série la plus EN RETARD commande : sur une formule bi-index, un mois
  // publié pour l'une seulement ne permet pas de calculer le coefficient.
  // Retourne null si AUCUN index n'est choisi, ou si l'un d'eux n'a aucune
  // valeur publiée (on ne borne alors sur rien — le socle prendra le relais).
  #ceiling() {
    const codes = this.#selectedCodes();
    if (codes.length === 0) return null;

    const months = codes.map((code) => this.publishedMonthsValue[code]).filter(Boolean);
    if (months.length !== codes.length) return null;

    const min = months.sort()[0];
    const [year, month] = min.split('-').map(Number);
    // ⚠️ La série limitante n'est nommée que si UNE SEULE est STRICTEMENT en
    // retard. Les 35 séries BT paraissent ensemble, donc l'égalité est le cas
    // NORMAL — désigner la première trouvée inventerait un coupable (retour QA
    // sur BT38 + BT02, tous deux à mai 2026). Miroir exact de la règle serveur.
    const laggards = codes.filter((c) => this.publishedMonthsValue[c] === min);
    const code = laggards.length === 1 && codes.length > 1 ? laggards[0] : null;
    return { year, month, code, codes };
  }

  #refreshMonthBounds() {
    if (!this.hasBaseMonthTarget || !this.hasBaseYearTarget) return;

    const codes = this.#selectedCodes();
    const ceiling = this.#ceiling();
    const disabled = codes.length === 0;

    this.baseMonthTarget.disabled = disabled;
    this.baseYearTarget.disabled = disabled;
    this.#toggle(this.monthPromptTarget, disabled);
    if (this.hasValuesBlockTarget) this.#toggle(this.valuesBlockTarget, !disabled);

    if (!ceiling) {
      this.#toggle(this.monthHintTarget, false);
      return;
    }

    this.#applyCeiling(ceiling);
    this.#renderHint(ceiling);
    this.#toggle(this.monthHintTarget, true);
  }

  // Retire les options hors borne des deux selects.
  //
  // ⚠️ L'option SÉLECTIONNÉE n'est JAMAIS retirée, même hors borne (D-M0-5) :
  // la retirer ferait glisser la sélection sur une autre valeur, donc
  // déplacerait silencieusement la base d'un marché déjà facturé. Un mois hors
  // borne resté sélectionné est signalé par l'avertissement, pas corrigé
  // d'autorité.
  #applyCeiling(ceiling) {
    Array.from(this.baseYearTarget.options).forEach((option) => {
      const year = Number(option.value);
      option.hidden = year > ceiling.year && !option.selected;
      option.disabled = option.hidden;
    });

    const selectedYear = Number(this.baseYearTarget.value);
    Array.from(this.baseMonthTarget.options).forEach((option) => {
      const month = Number(option.value);
      const over = selectedYear === ceiling.year && month > ceiling.month;
      option.hidden = over && !option.selected;
      option.disabled = option.hidden;
    });
  }

  // Trois formes, miroir exact du helper serveur `revision_publication_hint` :
  // une seule série / plusieurs avec UN retardataire nommé / plusieurs à égalité
  // (le cas normal — on ne nomme alors personne).
  #renderHint(ceiling) {
    const node = this.monthHintTarget;
    const single = ceiling.codes.length <= 1;
    let template;
    if (single) template = node.dataset.templateSingle;
    else if (ceiling.code) template = node.dataset.templateMulti;
    else template = node.dataset.templateMultiPlain;
    if (!template) return;

    node.textContent = template
      .replace('%{series}', single ? ceiling.codes[0] : ceiling.code)
      .replace('%{month}', this.#monthLabel(ceiling.year, ceiling.month));
  }

  // Libellé « juillet 2026 » lu dans le select lui-même : les noms de mois y
  // sont déjà rendus par `I18n.t('date.month_names')`, donc aucune liste
  // française n'entre dans ce fichier.
  #monthLabel(year, month) {
    const option = Array.from(this.baseMonthTarget.options).find((o) => Number(o.value) === month);
    return `${(option?.textContent || '').trim().toLowerCase()} ${year}`;
  }

  // --- Auto-remplissage INSEE ------------------------------------------------

  #autofillAllRows() {
    this.#visibleRows().forEach((row) => this.#autofillRow(row.dataset.termIndex));
  }

  // Récupère la valeur du store INSEE pour (série de la rangée, mois zéro) et la
  // pose dans « Valeur mois zéro », en basculant la pastille de provenance.
  //
  // ⚠️ D-M0-7 — SOCLE. Une absence VIDE désormais le champ s'il avait été
  // auto-rempli, là où l'ancien code le laissait tel quel. Ce résidu est le
  // défaut le plus grave du retour QA du 2026-08-07 : après un changement
  // d'index ou de mois, la modale affichait la valeur de l'ANCIEN couple (mesuré
  // : `143,2`, qui était BT49 de novembre 2023, sous un BT50 de novembre 2026),
  // et cette base divise TOUS les coefficients du marché. Une valeur SAISIE À LA
  // MAIN, elle, est conservée — la pastille INSEE est le discriminant.
  async #autofillRow(index) {
    if (index === undefined || !this.hasLookupUrlValue) return;

    const row = this.#valueRowFor(index);
    const valueInput = row?.querySelector("input[name*='[base_index_value]']");
    const insee = row?.querySelector("[data-price-revision-form-target='insee']");
    const series = this.#seriesCodeFor(index);
    const month = this.#referenceMonth();
    if (!row || !series || !month) {
      this.#toggleInsee(insee, false);
      return;
    }

    try {
      const url = new URL(this.lookupUrlValue, window.location.origin);
      url.searchParams.set('series_code', series);
      url.searchParams.set('month', month);
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        this.#dropInseeClaim(insee);
        return;
      }

      const data = await response.json();
      if (data.found && valueInput) {
        valueInput.value = data.value;
        this.#toggleInsee(insee, true);
        this.#hideWarning();
      } else {
        this.#clearAutofilled(valueInput, insee);
        this.#showWarning(series);
      }
      this.refresh();
    } catch {
      // Réseau indisponible : on laisse la saisie manuelle prendre le relais,
      // mais sans continuer d'affirmer une provenance INSEE (voir ci-dessous).
      this.#dropInseeClaim(insee);
    }
  }

  // ⚠️ UN ÉCHEC N'EST PAS UNE ABSENCE — on retire la PASTILLE, jamais la valeur.
  //
  // La pastille est une AFFIRMATION : « cette valeur vient de l'INSEE, pour ce
  // couple série × mois ». Après une requête qui n'a pas abouti (5xx, coupure)
  // on ne peut plus la tenir. La laisser allumée faisait croire que la valeur
  // affichée avait été vérifiée pour le NOUVEAU couple alors qu'elle appartient
  // au précédent — et cette base est le DIVISEUR de tous les coefficients du
  // marché. C'est la moitié « chemin d'échec » du socle D-M0-7, restée ouverte :
  // le garde-fou serveur (`base_index_value` presence + > 0) ne mord pas, la
  // valeur étant présente et positive, simplement fausse.
  //
  // ⚠️ ON NE VIDE PAS LE CHAMP, contrairement au cas « pas de valeur publiée » :
  // là le serveur a répondu et l'on SAIT que le couple n'a rien ; ici on ne sait
  // rien. Et le rendu serveur pose `auto_filled: term&.bt_series_code.present?`,
  // donc la pastille est allumée sur TOUTE valeur déjà enregistrée, saisie à la
  // main comprise : vider sur une coupure réseau détruirait le travail de
  // l'architecte pour un incident qui ne dit rien de sa valeur.
  #dropInseeClaim(insee) {
    this.#toggleInsee(insee, false);
  }

  // Ne vide QUE ce que l'auto-remplissage avait posé (pastille visible).
  #clearAutofilled(valueInput, insee) {
    const autoFilled = insee && !insee.classList.contains('hidden');
    if (autoFilled && valueInput) valueInput.value = '';
    this.#toggleInsee(insee, false);
  }

  // ⚠️ Le gabarit LONG ne se justifie que si le mois demandé DÉPASSE l'horizon
  // de publication — c'est le cas courant (l'INSEE paraît à ~3 mois), et nommer
  // le dernier mois publié dit alors quoi faire. Si le mois demandé lui est
  // ANTÉRIEUR, la série a un trou : annoncer « dernier mois publié : mai 2026 »
  // enverrait l'architecte vers une date qui n'est pas le problème. Le gabarit
  // COURT sert aussi la série sans aucune valeur publiée (absente du hash), qui
  // sortait auparavant SANS AUCUN avertissement — l'échec le plus muet des deux.
  #showWarning(series) {
    if (!this.hasMonthWarningTarget) return;

    const node = this.monthWarningTextTarget;
    const last = this.publishedMonthsValue[series];
    const asked = this.#referenceMonth()?.slice(0, 7);
    const beyond = last && asked && asked > last;
    const template = beyond ? node.dataset.template : node.dataset.templateShort;
    if (!template) return;

    let text = template
      .replace('%{series}', series)
      .replace('%{month}', this.#selectedMonthLabel());
    if (beyond) {
      const [year, month] = last.split('-').map(Number);
      text = text.replace('%{last}', this.#monthLabel(year, month));
    }
    node.textContent = text;
    this.#toggle(this.monthWarningTarget, true);
  }

  #hideWarning() {
    if (this.hasMonthWarningTarget) this.#toggle(this.monthWarningTarget, false);
  }

  #selectedMonthLabel() {
    return this.#monthLabel(Number(this.baseYearTarget.value), Number(this.baseMonthTarget.value));
  }

  #seriesCodeFor(index) {
    const row = Array.from(this.rowsTarget.querySelectorAll('tr')).find(
      (node) => node.dataset.termIndex === String(index)
    );
    return row?.querySelector("select[name*='[bt_series_code]']")?.value;
  }

  // Mois zéro au format `YYYY-MM-01` à partir des deux selects de l'en-tête.
  #referenceMonth() {
    const month = this.hasBaseMonthTarget ? this.baseMonthTarget.value : null;
    const year = this.hasBaseYearTarget ? this.baseYearTarget.value : null;
    if (!month || !year) return null;
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  // Bascule la pastille INSEE (nœud rendu serveur, porte UNIQUEMENT `hidden`).
  #toggleInsee(node, visible) {
    this.#toggle(node, visible);
  }

  // ⚠️ TOUJOURS la CLASSE `hidden`, jamais l'attribut booléen : le
  // `[hidden]{display:none}` de la feuille UA survivrait au retrait de la
  // classe et le nœud ne réapparaîtrait plus, sans la moindre erreur.
  #toggle(node, visible) {
    if (node) node.classList.toggle('hidden', !visible);
  }

  #toNumber(value) {
    const parsed = parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  #fr(value) {
    return String(Math.round(value * 1000) / 1000).replace('.', ',');
  }
}
